import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type BoardDetail,
  type Card,
  classifyCard,
  DeadlineBadge,
  type DeadlineState,
  displayColumnName,
  hiddenCategories,
  isUndated,
  type Label,
  scopeKey,
  useBoardTimeline,
  useSetCardCompleted,
  useStoredVisibility,
  VisibilityToggles,
} from '@/entities/board';
import { useViewer } from '@/entities/viewer';
import { addDays, todayIn, weekOf } from '@/shared/lib/calendar';
import { formatWeekRange, truncate } from '@/shared/lib/datetime';
import { DayCalendar, ErrorState, FilterPopover, LoadingState } from '@/shared/ui';

import styles from './Timeline.module.css';

/** Chronological groups; the server decides which one a card belongs to. */
const GROUPS: readonly DeadlineState[] = ['overdue', 'today', 'tomorrow', 'later', 'past'];

/** Tasks without any date are listed apart; this many are shown (a long list is not useful). */
const UNDATED_LIMIT = 50;

/** Column names longer than this are cut with an ellipsis (the full name is the tooltip). */
export const COLUMN_NAME_MAX = 30;

interface Props {
  detail: BoardDetail;
  onOpenCard: (cardId: string) => void;
  onClose: () => void;
}

/**
 * Timeline of THIS board for one week at a time (Monday..Sunday, current week by default).
 * Cards with a deadline are grouped Overdue / Today / Tomorrow / Later (soonest first) along a
 * vertical time axis. Overdue cards are always listed, whatever week is shown, so they never
 * silently drop out. A card with a start is placed by its whole period. Completed cards are
 * shown by default (visually muted) and are never overdue; overdue ones can be hidden. Cards open in the regular card dialog.
 */
export function TimelineDrawer({ detail, onOpenCard, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const boardId = detail.board.id;
  const viewer = useViewer();
  const zone = viewer.status === 'authenticated' ? viewer.user.timezone : null;
  const [anchor, setAnchor] = useState(() => todayIn(zone));
  const [calendarOpen, setCalendarOpen] = useState(false);
  // The four switches are remembered per board (another board never inherits them).
  const [visibility, setVisibility, resetVisibility] = useStoredVisibility(
    scopeKey({ view: 'timeline', kind: 'board', id: boardId }),
  );
  const includeCompleted = visibility.showCompleted;
  const week = weekOf(anchor);
  const today = todayIn(zone);
  const isCurrentWeek = weekOf(today).first === week.first;
  const timeline = useBoardTimeline(boardId, includeCompleted, week, true);
  const setCompleted = useSetCardCompleted(boardId);
  const closeRef = useRef<HTMLButtonElement>(null);
  const calendarButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  // Overdue cards always come back from the server; the switches only hide items here. A card is
  // an event (explicit start and end) or a task (deadline only): never both.
  const cards = (timeline.data ?? []).filter((card) => {
    if (!visibility.showOverdue && card.is_overdue) return false;
    return classifyCard(card) === 'event' ? visibility.showEvents : visibility.showTasks;
  });
  const undatedCards = visibility.showTasks
    ? detail.cards.filter((card) => isUndated(card) && (includeCompleted || !card.completed_at))
    : [];
  const columnName = (card: Card) => {
    const column = detail.columns.find((c) => c.id === card.placement.column_id);
    return column ? displayColumnName(column, t('columns.calendar')) : '';
  };
  const labelsOf = (card: Card): Label[] =>
    card.label_ids
      .map((id) => detail.labels.find((label) => label.id === id))
      .filter((label): label is Label => label !== undefined);
  const range = formatWeekRange(week.first, week.last, i18n.language);

  return (
    <aside
      className={styles.panel}
      aria-label={t('timeline.title')}
      data-testid="timeline"
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        if (calendarOpen) {
          setCalendarOpen(false);
          calendarButton.current?.focus();
        } else {
          onClose();
        }
      }}
    >
      <header className={styles.header}>
        <h2 className={styles.title}>{t('timeline.title')}</h2>
        <button
          ref={closeRef}
          type="button"
          className={styles.close}
          aria-label={t('timeline.close')}
          title={t('timeline.close')}
          onClick={onClose}
        >
          ×
        </button>
      </header>

      <div className={styles.nav} role="group" aria-label={t('timeline.weekNav')}>
        <div className={styles.navGroup}>
          <button
            type="button"
            className={styles.navButton}
            aria-label={t('timeline.previousWeek')}
            title={t('timeline.previousWeek')}
            onClick={() => {
              setAnchor(addDays(week.first, -7));
            }}
          >
            ←
          </button>
          <span className={styles.range} data-testid="timeline-range" aria-live="polite">
            {range}
          </span>
          <button
            type="button"
            className={styles.navButton}
            aria-label={t('timeline.nextWeek')}
            title={t('timeline.nextWeek')}
            onClick={() => {
              setAnchor(addDays(week.first, 7));
            }}
          >
            →
          </button>
        </div>
        <div className={styles.toolGroup}>
          <button
            type="button"
            className={styles.todayButton}
            disabled={isCurrentWeek}
            onClick={() => {
              setAnchor(today);
            }}
          >
            {t('timeline.today')}
          </button>
          <button
            ref={calendarButton}
            type="button"
            className={styles.navButton}
            aria-label={t('timeline.pickDate')}
            title={t('timeline.pickDate')}
            aria-expanded={calendarOpen}
            aria-controls="timeline-calendar"
            onClick={() => {
              setCalendarOpen((open) => !open);
            }}
          >
            📅
          </button>
        </div>
      </div>
      {calendarOpen && (
        <div id="timeline-calendar" className={styles.calendar}>
          <DayCalendar
            value={anchor}
            week={week}
            autoFocus
            onSelect={(day) => {
              setAnchor(day);
              setCalendarOpen(false);
              calendarButton.current?.focus();
            }}
          />
        </div>
      )}

      <div className={styles.toggles}>
        <FilterPopover activeCount={hiddenCategories(visibility)} onReset={resetVisibility}>
          <VisibilityToggles value={visibility} onChange={setVisibility} />
        </FilterPopover>
      </div>

      <div className={styles.body}>
        {timeline.isPending && <LoadingState />}
        {timeline.isError && (
          <ErrorState error={timeline.error} onRetry={() => void timeline.refetch()} />
        )}
        {timeline.data && cards.length === 0 && undatedCards.length === 0 && (
          <p className={styles.empty}>{t('timeline.empty')}</p>
        )}
        {GROUPS.map((state) => {
          const group = cards.filter(
            (card) => (card.is_overdue ? 'overdue' : card.deadline?.state) === state,
          );
          if (group.length === 0) return null;
          return (
            <section
              key={state}
              className={`${styles.group} ${styles[state]}`}
              aria-labelledby={`timeline-${state}`}
              data-testid={`timeline-group-${state}`}
            >
              <h3 id={`timeline-${state}`} className={styles.groupTitle}>
                {t(`timeline.groups.${state}`)}
                <span className={styles.count}>{group.length}</span>
              </h3>
              <ul className={styles.list}>
                {group.map((card) => {
                  const done = card.completed_at !== null;
                  const column = columnName(card);
                  const shortColumn = truncate(column, COLUMN_NAME_MAX);
                  const kind = classifyCard(card);
                  return (
                    <li
                      key={card.id}
                      className={`${styles.item} ${styles[kind]} ${done ? styles.done : ''}`}
                      data-testid="timeline-item"
                      data-kind={kind}
                    >
                      <button
                        type="button"
                        className={`${styles.check} ${done ? styles.checkOn : ''}`}
                        aria-pressed={done}
                        aria-label={t(done ? 'cards.markIncomplete' : 'cards.markComplete')}
                        title={t(done ? 'cards.markIncomplete' : 'cards.markComplete')}
                        onClick={(event) => {
                          setCompleted.mutate({
                            cardId: card.id,
                            completed: !done,
                            anchor: event.currentTarget,
                          });
                        }}
                      >
                        {done ? '✓' : ''}
                      </button>
                      <div className={styles.main}>
                        <button
                          type="button"
                          className={styles.itemTitle}
                          data-testid="timeline-item-title"
                          onClick={() => {
                            onOpenCard(card.id);
                          }}
                        >
                          {card.title}
                        </button>
                        <div className={styles.meta}>
                          <span className={styles.kind} data-testid="timeline-item-kind">
                            <span aria-hidden>{kind === 'event' ? '▭' : '☑'}</span>
                            {t(`timeline.kind.${kind}`)}
                          </span>
                          <DeadlineBadge card={card} />
                          {column && (
                            <span
                              className={styles.column}
                              data-testid="timeline-item-column"
                              title={shortColumn === column ? undefined : column}
                            >
                              {shortColumn === column ? (
                                column
                              ) : (
                                <>
                                  <span aria-hidden>{shortColumn}</span>
                                  <span className="visually-hidden">{column}</span>
                                </>
                              )}
                            </span>
                          )}
                          {labelsOf(card).length > 0 && (
                            <span className={styles.labels}>
                              {labelsOf(card).map((label) => (
                                <span
                                  key={label.id}
                                  className={styles.dot}
                                  style={{ background: label.color }}
                                  title={label.name || t('labels.unnamed')}
                                  role="img"
                                  aria-label={label.name || t('labels.unnamed')}
                                />
                              ))}
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
        {undatedCards.length > 0 && (
          <section
            className={`${styles.group} ${styles.undated}`}
            aria-labelledby="timeline-undated"
            data-testid="timeline-group-undated"
          >
            <h3 id="timeline-undated" className={styles.groupTitle}>
              {t('timeline.groups.undated')}
              <span className={styles.count}>{undatedCards.length}</span>
            </h3>
            <ul className={styles.list}>
              {undatedCards.slice(0, UNDATED_LIMIT).map((card) => (
                <li
                  key={card.id}
                  className={`${styles.item} ${styles.task} ${card.completed_at ? styles.done : ''}`}
                  data-testid="timeline-item"
                  data-kind="task"
                >
                  <div className={styles.main}>
                    <button
                      type="button"
                      className={styles.itemTitle}
                      data-testid="timeline-item-title"
                      onClick={() => {
                        onOpenCard(card.id);
                      }}
                    >
                      {card.title}
                    </button>
                    <div className={styles.meta}>
                      <span className={styles.kind}>
                        <span aria-hidden>☑</span>
                        {t('timeline.kind.task')}
                      </span>
                      <span className={styles.column}>{columnName(card)}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            {undatedCards.length > UNDATED_LIMIT && (
              <p className={styles.empty}>
                {t('timeline.undatedMore', { count: undatedCards.length - UNDATED_LIMIT })}
              </p>
            )}
          </section>
        )}
      </div>
    </aside>
  );
}
